import { type Action, combineReducers, configureStore } from "@reduxjs/toolkit"
import {
  createTransform,
  getStoredState,
  type PersistConfig,
  type PersistedState,
  type Persistor,
  persistReducer,
  persistStore
} from "redux-persist"
import { type REHYDRATE } from "redux-persist/es/constants"
import storage from "redux-persist/lib/storage"

import { rootReducer } from "./root-reducer.js"
import type { CalendarState } from "./containers/Calendar/redux/calendar.types.ts"
import {
  rehydrateWidgetsSnapshot,
  serializeWidgetsSnapshot,
  type WidgetsSliceState
} from "./containers/DashboardPage/redux/widgets.slice.ts"
import { type UserUIPreferencesState } from "./containers/User/redux/ui-preferences.types.ts"
import { leadsSliceName } from "./containers/Leads/redux/leads.constants.ts"
import type { LeadsState } from "./containers/Leads/redux/leads.types.ts"
import { projectsSliceName } from "./containers/Projects/redux/projects.constants.ts"
import type { ProjectsState } from "./containers/Projects/redux/projects.types.ts"
import { teamMembersSliceName } from "./containers/TeamManagementPage/redux/team-members.constants.ts"
import type { TeamMembersState } from "./containers/TeamManagementPage/redux/team-members.types.ts"

const combinedReducer = combineReducers(rootReducer)

const widgetsTransform = createTransform<WidgetsSliceState, unknown>(
  (state) => serializeWidgetsSnapshot(state),
  (snapshot) => rehydrateWidgetsSnapshot(snapshot),
  { whitelist: ["widgets"] }
)

// `loaded` is a fact about this page load, not about the user: persisting it true
// would let a returning browser decide from a stale value instead of waiting for
// the fetch that carries a choice made on another device.
const uiPreferencesTransform = createTransform<UserUIPreferencesState, unknown>(
  (state) => ({ ...state, loaded: false }),
  (stored) => stored as UserUIPreferencesState,
  { whitelist: ["userUIPreferences"] }
)

// `calendar` and the three list slices are here for their filters alone — a
// transform below strips everything else from each, so the whitelist's
// "no server-owned data" rule holds.
const PERSIST_WHITELIST = [
  "auth",
  "nav",
  "widgets",
  "userUIPreferences",
  "calendar",
  leadsSliceName,
  projectsSliceName,
  teamMembersSliceName
]

const persistedSliceKeys =
  <S extends object>(keys: ReadonlyArray<keyof S>) =>
  (state: Partial<S> | undefined): Partial<S> => {
    if (!state) return {}
    const kept: Partial<S> = {}
    keys.forEach((key) => {
      if (key in state) kept[key] = state[key]
    })
    return kept
  }

const leadsFilters = persistedSliceKeys<LeadsState>(["filters"])
const leadsTransform = createTransform<Partial<LeadsState>, Partial<LeadsState>>(
  leadsFilters,
  leadsFilters,
  { whitelist: [leadsSliceName] }
)

const projectsFilters = persistedSliceKeys<ProjectsState>([
  "listFilters",
  "gridFilters"
])
const projectsTransform = createTransform<
  Partial<ProjectsState>,
  Partial<ProjectsState>
>(projectsFilters, projectsFilters, { whitelist: [projectsSliceName] })

const teamMembersFilters = persistedSliceKeys<TeamMembersState>(["filters"])
const teamMembersTransform = createTransform<
  Partial<TeamMembersState>,
  Partial<TeamMembersState>
>(teamMembersFilters, teamMembersFilters, {
  whitelist: [teamMembersSliceName]
})

// The Calendar persists the filters the user chose and nothing else. An open
// dialog is not session state: `selectedEvent` is the whole event behind the
// detail modal, so storing it reopened that modal on every later visit to the
// Calendar — including one the user never chose to leave open, after a crash.
//
// An allowlist rather than a list of keys to drop, so a field added to the
// slice later is not persisted by default. Applied outbound as well as inbound,
// so a browser already holding a selected event recovers on the next load
// instead of waiting for the next write.
const persistedCalendarState = (
  state: Partial<CalendarState> | undefined
): Partial<CalendarState> => (state ? { filters: state.filters } : {})

const calendarTransform = createTransform<
  Partial<CalendarState>,
  Partial<CalendarState>
>(persistedCalendarState, persistedCalendarState, { whitelist: ["calendar"] })

// The default getStoredState rehydrates every key found in the blob — the
// whitelist only filters writes — so the read is filtered here instead of
// rewriting the blob up front: a boot-time rewrite raced other tabs and could
// restore an already-rotated refresh token. The blob itself shrinks to the
// whitelisted keys on the first regular persist write.
const readWhitelistedStoredState = async (
  config: PersistConfig<ReturnType<typeof combinedReducer>>
): Promise<PersistedState> => {
  const stored = await getStoredState(config)
  if (!stored) return stored
  return Object.fromEntries(
    Object.entries(stored).filter(
      ([key]) => key === "_persist" || PERSIST_WHITELIST.includes(key)
    )
  ) as PersistedState
}

const persistConfig: PersistConfig<ReturnType<typeof combinedReducer>> = {
  key: "ez_build",
  version: 2,
  storage,
  whitelist: PERSIST_WHITELIST,
  getStoredState: readWhitelistedStoredState,
  transforms: [
    widgetsTransform,
    uiPreferencesTransform,
    calendarTransform,
    leadsTransform,
    projectsTransform,
    teamMembersTransform
  ]
}

const persistedReducer = persistReducer(persistConfig, combinedReducer)

interface StoreConfig {
  store: ReturnType<typeof configureStore>
  persistor: Persistor
}

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      thunk: true,
      serializableCheck: false
    }),
  // .concat(routerMiddleware as any),
  devTools: process.env.NODE_ENV !== "production"
})

export default async (): Promise<StoreConfig> => {
  // const history = createReduxHistory(store)
  const persistor = persistStore(store)

  // Storage failure must not stop the app from rendering — boot with initial
  // state instead.
  try {
    const reducerVersion = await storage.getItem("reducerVersion")
    const version = persistConfig.version?.toString() as string

    if (reducerVersion !== version) {
      await persistor.purge()
      await storage.setItem("reducerVersion", version)
    }
  } catch {
    // handled by booting unpersisted
  }

  return {
    store,
    persistor
    // history
  }
}
// type RootState = ReturnType<typeof store.getState>
type RootState = ReturnType<typeof combinedReducer>
type RehydrateAction = Action<typeof REHYDRATE> & {
  key: string
  payload: RootState
  err: unknown
}
type AppDispatch = typeof store.dispatch
type AppDispatchWithRouter = AppDispatch
// &  ThunkDispatch<RootState, unknown, AnyAction | RouterAction>
type AppStore = typeof store

export type {
  StoreConfig,
  RootState,
  RehydrateAction,
  AppStore,
  AppDispatch,
  AppDispatchWithRouter
}
